import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { useBlocker, useLocation } from 'react-router-dom';
import { useAppSelector , useAppDispatch } from '@/store';
import { setCanNavigate as setCanNavigateAction } from '@/modules/casino/store/actions/navigation';

type ExitIntentHandlerProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    popupID?: string;
    isBlockRouteReloadEnabled?: boolean;
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const ExitIntentHandler = (componentProps: ExitIntentHandlerProps) => {
  let props = componentProps;
  const dispatch = useAppDispatch();

  const dataElementContext = useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const templatePopupID = props.properties?.popupID;
  const isBlockRouteReloadEnabled = props.properties?.isBlockRouteReloadEnabled;
  const whitelistedRoutes = window.config?.whitelistedRoutes || {};

  const { canNavigate } = useAppSelector((state: any) => state.navigation || { canNavigate: false });

  // if this module is loaded then set default to not allowing navigation
  useEffect(() => {
    dispatch(setCanNavigateAction(false));
  }, [dispatch]);

  const [showPopup, setShowPopup] = useState(false);
  const location = useLocation();

  const blockedRoutes = window.config?.blockedRoutes;
  const blockedRoute = blockedRoutes?.find((route) => route.path === location.pathname);

  const isRouteWhitelisted = (targetPath: string) => {
    return Object.values(whitelistedRoutes).some(route => {
      if (route.includes('*')) {
        const pattern = route.replace('*', '.*');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(targetPath);
      }
      return targetPath === route || targetPath.startsWith(route);
    });
  };

  const blocker = useBlocker(({ nextLocation }) => {
    const targetPath = nextLocation.pathname;
    
    if (isRouteWhitelisted(targetPath)) {
      // console.log('Route is whitelisted, allowing navigation:', targetPath);
      return false;
    }

    if (blockedRoute?.popupID === templatePopupID) {
      if (!canNavigate) {
        setShowPopup(true);
        return true;
      }
    }
    return false; 
  });

  useEffect(() => {
    // Handle the beforeunload event for reload
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (blockedRoute?.popupID === templatePopupID) {
        e.preventDefault();
        e.returnValue = '';
        setShowPopup(true);
      }
      return null;
    };
    
    if (isBlockRouteReloadEnabled) {
      window.addEventListener('beforeunload', handleBeforeUnload, { capture: true });
    }

    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload, { capture: true });
    };
  }, [blockedRoute, templatePopupID, isBlockRouteReloadEnabled]);

  const closePopupHandler = () => {
    setShowPopup(false);
  };

  const continuePopupHandler = () => {
    setShowPopup(false);
    if (blocker.proceed) {
      blocker?.proceed();
    } else {
      window.location.reload();
    }
  };

  const contextValue = useMemo(() => {
    return {
      ...dataElementContext,
      showPopup,
      closePopupHandler,
      continuePopupHandler,
      whitelistedRoutes,
      isRouteWhitelisted,
    };
  }, [dataElementContext, showPopup, closePopupHandler, continuePopupHandler, whitelistedRoutes]);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default ExitIntentHandler;

